import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { usePWA } from '../../context/PWAContext';

function PWAInstallBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const timerRef = useRef(null);
  const { hasShownBannerThisSession, setHasShownBannerThisSession, isEnabled } = usePWA();

  useEffect(() => {
    // First check if banner is globally enabled
    if (!isEnabled) return;

    // If we've shown the banner during this session (navigation), don't show it again
    if (hasShownBannerThisSession) return;

    // Only show on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) return;

    // Check if already installed
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
    if (isInstalled) return;

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(isIOSDevice);

    // Show banner
    setShowBanner(true);
    setHasShownBannerThisSession(true);

    // Start the 10-second timer
    timerRef.current = setTimeout(() => {
      if (!showIOSModal) {
        setShowBanner(false);
      }
    }, 10000);

    // Handle beforeinstallprompt event for non-iOS devices
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [hasShownBannerThisSession, setHasShownBannerThisSession, showIOSModal, isEnabled]);

  const handleCloseBanner = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setShowBanner(false);
  };

  const handleCloseIOSModal = () => {
    setShowIOSModal(false);
    setShowBanner(false);
  };

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSModal(true);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    } else if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const choiceResult = await deferredPrompt.userChoice;
        if (choiceResult.outcome === 'accepted') {
          setShowBanner(false);
        }
      } catch (error) {
        console.error('Error installing PWA:', error);
      }
    }
  };

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 transform transition-transform duration-300 ease-in-out z-50">
        <div className="flex items-center justify-between max-w-md mx-auto relative">
          <div className="flex items-center flex-grow mr-4">
            <AddToHomeScreenIcon className="mr-2" />
            <span>Add to Home Screen for a better experience!</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleInstall}
              className="bg-white text-blue-600 px-4 py-1 rounded-full text-sm font-semibold hover:bg-blue-50 transition-colors duration-200"
            >
              Install
            </button>
            <button
              onClick={handleCloseBanner}
              className="p-1 hover:bg-blue-500 rounded-full transition-colors duration-200"
              aria-label="Close banner"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>

      {/* iOS Installation Modal */}
      <Dialog
        open={showIOSModal}
        onClose={handleCloseIOSModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add to Home Screen on iOS
          <IconButton
            aria-label="close"
            onClick={handleCloseIOSModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="space-y-4">
            <p>To add and use this web app from your iOS device home screen:</p>
            <ol className="space-y-4">
              <li>
                <div className="flex items-start">
                  <span className="mr-2">1.</span>
                  <div className="flex flex-col">
                    <div className="flex items-center">
                      <span>Tap the Share button</span>
                      <IosShareIcon className="mx-2 text-blue-500" />
                    </div>
                    <span className="text-sm text-gray-400">Located in Safari's toolbar at the bottom (or in chrome, at the top right)</span>
                  </div>
                </div>
              </li>
              <li>
                <div className="flex items-start">
                  <span className="mr-2">2.</span>
                  <div className="flex flex-col">
                    <div className="flex items-center">
                      <span>Scroll down and tap "Add to Home Screen"</span>
                      <AddBoxIcon className="mx-2 text-blue-500" />
                    </div>
                    <span className="text-sm text-gray-400">It will be one of the menu options.</span>
                  </div>
                </div>
              </li>
              <li>
                <div className="flex items-start">
                  <span className="mr-2">3.</span>
                  <div className="flex items-center">
                    <span>Tap "Add" in the top-right corner</span>
                  </div>
                </div>
              </li>
            </ol>
            <div className="mt-6 p-3 bg-blue-500 bg-opacity-10 rounded-lg">
              <p className="text-sm text-gray-300">
                Once installed, you'll be able to access the app directly from your home screen!
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PWAInstallBanner; 